import { createContext, useContext, useState, useEffect } from "react";
import { STORAGE_KEYS } from "@/config/storageKeys";
import { API_URL } from "@/config/api";
import axios from "axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEYS.TOKEN));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, referral_code = null) => {
    const payload = { email, password, name };
    if (referral_code) payload.referral_code = referral_code;
    const response = await axios.post(`${API_URL}/api/auth/register`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const googleLogin = async (googleToken) => {
    const response = await axios.post(`${API_URL}/api/auth/google`, {
      token: googleToken,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const appleLogin = async (identityToken, name = null) => {
    // Apple ships user.name only on the very first sign-in, so we
    // forward whatever the SDK gave us. Server falls back gracefully.
    const response = await axios.post(`${API_URL}/api/auth/apple`, {
      identity_token: identityToken,
      name,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    setToken(null);
    setUser(null);
  };

  // Re-fetch the current user (e.g. after a checkout completes and the
  // LemonSqueezy webhook has activated the new plan). Returns the fresh user
  // or null; never throws so callers can poll it safely.
  const refreshUser = async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("refreshUser failed:", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        googleLogin,
        appleLogin,
        logout,
        refreshUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
